/**
=========================================================
* Soft UI Dashboard React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, forwardRef } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import DataTable from 'react-data-table-component';
import Autocomplete from '@mui/material/Autocomplete';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import SoftButton from "components/SoftButton";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import TimelineItem from "examples/Timeline/TimelineItem";

import { useSoftUIController, driverUpdated, setSelectedDriver, setWaypoints } from "context";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import { assignTasks, getDirections } from 'actions'

//data
import pickupColumns from "./pickupColumns";
import deliveryColumns from "./deliveryColumns";

function DriverDetail(props) {
  let { drivers, deliveries, pickups, selectedDriver } = props;
  const [currentTab, setCurrentTab] = useState(0);
  const [controller, dispatch] = useSoftUIController();
  const [toaster, setToaster] = useState(false);
  const [modifiedTasks, setModifiedTasks] = useState([]);
  const [deliveryData, setDeliveryData] = useState([]);
  const [pickupData, setPickupData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const { user, currentDate } = controller;

  useEffect(() => {
    setDeliveryData([].concat(deliveries));
    console.log('setting deliveries', deliveries);
    setPickupData([].concat(pickups));
  }, [deliveries, pickups]);

  useEffect(() => {
    console.log('modified tasks', modifiedTasks);
  }, [modifiedTasks]);

  useEffect(() => {
    if (selectedDriver && !selectedDriver.directions) {
      let driverDeliveries = deliveries.filter(d => d.driver === selectedDriver.email);
      let driverPickups = pickups.filter(p => p.driver === selectedDriver.email);

      let tasks = driverDeliveries.concat(driverPickups);
      let coords = selectedDriver.coords ? selectedDriver.coords : null;

      console.log('getting waypointss for', tasks, coords, selectedDriver.shipFrom);
      getDirections(tasks, coords, selectedDriver.shipFrom).then(directions => {
        console.log('getting waypointss', directions);
        setWaypoints(dispatch, selectedDriver, directions);
      }).catch(err => {
        console.error(err.message);
      });
    }
  }, [selectedDriver]);

  const modifyDelivery = (delivery, assigned) => {
    let existing = deliveries.find(d => d.orders[0].id === delivery.orders[0].id);
    if((assigned && existing.driver === selectedDriver.email) || (!assigned && existing.driver === null)) { //task was switched back to its original state, remove from the modified tasks
      setModifiedTasks(modifiedTasks.filter(t => t.task.orders[0].id !== delivery.orders[0].id));
    }
    else {
      setModifiedTasks([ ...modifiedTasks, { task: delivery, assigned }]);
    }

    if(assigned) {
      setDeliveryData(deliveryData.map(d => d.orders[0].id === delivery.orders[0].id ? { ...d, driver: selectedDriver.email} : d));
    }
    else {
      setDeliveryData(deliveryData.map(d => d.orders[0].id === delivery.orders[0].id ? { ...d, driver: null} : d));
    }
    
  }

  const modifyPickup = (pickup, assigned) => {  
    let existing = pickups.find(p => p.returnAuthId === pickup.returnAuthId);    
    if((assigned && existing.driver === selectedDriver.email) || (!assigned && existing.driver === null)) { //task was switched back to its original state, remove from the modified tasks
      console.log('removing task');
      setModifiedTasks(modifiedTasks.filter(t => t.task.returnAuthId !== pickup.returnAuthId));
    }
    else {
      setModifiedTasks([ ...modifiedTasks, { task: pickup, assigned }]);
    }

    if(assigned) {
      setPickupData(pickupData.map(p => p.returnAuthId === pickup.returnAuthId ? { ...p, driver: selectedDriver.email} : p));
    }
    else {
      setPickupData(pickupData.map(p => p.returnAuthId === pickup.returnAuthId ? { ...p, driver: null} : p));
    }
  }

  

  const generateTaskList = () => {
    let tasks = [];
    let driverDeliveries = deliveries.filter(d => d.driver === selectedDriver.email);
    let driverPickups = pickups.filter(p => p.driver === selectedDriver.email);
    let temp = driverDeliveries.concat(driverPickups);  
    if(temp.length)
    {
      selectedDriver.directions.order.forEach(o => {
        let task = temp.find(t => t.taskId === selectedDriver.directions.tasks[o]);
        if(task)
        {
          tasks.push(task);
        }        
      });  
    }  
       
    //console.log('got tasks', tasks, 'with temp', temp, 'and directions', selectedDriver.directions);
    if(tasks.length)
    {
      return (
        <SoftBox p={2}>
          {tasks.map((t, i) => (
            <TimelineItem
              key={`task_${i}`}
              color={t.completed ? 'success' : 'error'}
              icon={t.returnAuthId ? 'view_in_ar' : 'directions_bus'}
              title={t.returnAuthId ? `Pickup - ${t.customer.company}` : `Delivery - ${t.customer.company}`}
              description={`${t.address.address1} ${t.address.city}, ${t.address.state} ${t.address.zip}`}
              dateTime={t.completed ? t.completedDate : 'TASK INCOMPLETE'}
            />
          ))}
        </SoftBox>
      );
    }
    else {
      return (
        <div style={{ width: '100%', textAlign: 'center' }}>
          <SoftTypography variant="caption" fontWeight="light" color="primary">
            There are no tasks assigned to this driver
          </SoftTypography>
        </div>
      );
    }
  }

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToaster(false);
  };

  return (
    <Card>
      <SoftBox p={2}>
        <Grid container spacing={3}>
          <Grid item lg={9}>
            <SoftBox display="flex" flexDirection="column" height="100%">
              <SoftTypography variant="body2" color="text">
                Select a driver to manage their task assignment
              </SoftTypography>
              <Grid item lg={3}>
                <Autocomplete
                  id="inactive_drivers"
                  onChange={(e, value) => {
                    setSelectedDriver(dispatch, value);
                  }}
                  options={drivers.filter(d => d.driver && (d.shipFrom === user?.shipFrom))}
                  value={selectedDriver}
                  sx={{ width: 300, marginTop: '20px', marginBottom: '20px' }}
                  renderInput={(params) => { return <TextField style={{ fontSize: '1rem' }} {...params} label=" " /> }}
                />
              </Grid>
              <Grid item lg={12}>
                <FormGroup style={{ paddingLeft: '20px' }}>
                  <FormControlLabel control={<Checkbox inputProps={{ 'aria-label': 'controlled' }} checked={showCompleted} onChange={(e) => { setShowCompleted(e.target.checked); }} />} label="Show Completed Tasks" />
                </FormGroup>
                <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)}>
                  <Tab label="Deliveries" {...a11yProps(0)} />
                  <Tab label="Pick-Ups" {...a11yProps(1)} />
                </Tabs>
                <TabPanel value={currentTab} index={0}>
                  {selectedDriver ? <DataTable
                    columns={deliveryColumns(modifyDelivery, drivers, selectedDriver, deliveryData)}
                    data={deliveryData.filter(d => {
                      if (selectedDriver.shipFrom === 'PGH') {
                        if (showCompleted) {
                          return d.deliveryMethod === 'FEI Delivery';
                        }
                        else {
                          return d.deliveryMethod === 'FEI Delivery' && !d.completed;
                        }
                    }
                    else if(selectedDriver.shipFrom === 'PA2')
                    {
                      if(showCompleted)
                      {
                        return d.deliveryMethod === `BEX2 - FEI Delivery`;
                      }
                      else
                      {
                        return d.deliveryMethod === `BEX2 - FEI Delivery` && !d.completed;
                      }
                    }
                    else
                    {
                       if(showCompleted)
                        {
                          return d.deliveryMethod === `B${selectedDriver.shipFrom} - FEI Delivery`;
                        }
                        else
                        {
                          return d.deliveryMethod === `B${selectedDriver.shipFrom} - FEI Delivery` && !d.completed;
                        }
                      }
                    })}
                    dense
                  /> : <div style={{ width: '100%', textAlign: 'center' }}>Select a driver to view deliveries</div>}
                </TabPanel>
                <TabPanel value={currentTab} index={1}>
                  {selectedDriver ? <DataTable
                    columns={pickupColumns(modifyPickup, pickupData, selectedDriver, drivers)}
                    data={pickupData.filter(p => {
                      if (showCompleted) {
                        return p.division === selectedDriver.shipFrom
                      }
                      else {
                        return p.division === selectedDriver.shipFrom && !p.completed;
                      }
                    })}
                    dense
                  /> : <div style={{ width: '100%', textAlign: 'center' }}>Select a driver to view pick-ups</div>}
                </TabPanel>
              </Grid>
              <Grid item lg={6}>
                <SoftButton disabled={loading} onClick={() => {
                  if (selectedDriver) {
                    setLoading(true);
                    assignTasks(selectedDriver, user.email, modifiedTasks, currentDate).then(res => {     

                      let updatedDeliveries = deliveryData.map(d => {
                        let delivery = res.deliveries.find(rd => d.orders[0].id === rd.orders[0].id);                       
                        return delivery ? delivery : d;
                      });

                      let updatedPickups = pickupData.map(p => {
                        let pickup = res.pickups.find(rp => rp.returnAuthId === p.returnAuthId);
                        return pickup ? pickup : p;
                      });

                      setModifiedTasks([]);
                      setLoading(false);
                      driverUpdated(dispatch, { deliveries: updatedDeliveries, pickups: updatedPickups });
                      setToaster(true);
                    });
                  }
                  else {
                    alert('You must select a driver to manage');
                  }
                }}>Update Driver Tasks</SoftButton>
              </Grid>
            </SoftBox>
          </Grid>
          {selectedDriver && selectedDriver.directions ? (<Grid style={{ display: selectedDriver ? 'flex' : 'none' }} item lg={3}>
            <Grid item lg={12}>
              <SoftTypography sx={{ textAlign: 'center' }} variant="h5" fontWeight="bold" color="dark">
                Task Summary
              </SoftTypography>
              <Grid item lg={12}>
                {generateTaskList()}
              </Grid>
            </Grid>
          </Grid>) : ''}
        </Grid>
      </SoftBox>
      <Snackbar open={toaster} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
          The operation completed successfully!
        </Alert>
      </Snackbar>
    </Card>
  );
}


const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <SoftBox sx={{ p: 3, paddingLeft: '0px', paddingRight: '0px' }}>
          {children}
        </SoftBox>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default DriverDetail;
